// Below is a mapping of raw encounter statuses returned from the backend to the statuses used in the frontend components.
// Front end status mappings to display values are defined for each encounter card component separately.
import {
  ADMITTED, CLOSED,
  DECEASED,
  DISCHARGED,
  PAYOR_CHANGED,
  PRESENTED,
  TRANSFERRED
} from "@manual_encounters/constants/PingStatuses";
import {
  AcceptedStatuses, ReferralStatus,
  ReferralStatuses, ShowStatuses
} from "@clients/profile_page/summary_tab/timeline_components/ReferralCard.constants";
import {CrisisStatus, CrisisStatuses} from "@clients/profile_page/summary_tab/timeline_components/CrisisCard.constants";
import {SDOHStatus, SDOHStatuses} from "@clients/profile_page/summary_tab/timeline_components/SDOHCard.constants";
import {Status} from "@clients/profile_page/summary_tab/timeline_components/ADTCard.constants";

export const EncounterStatusMap: Record<string, string> = {
  // ADT statuses
  "ADT_ADMITTED" : ADMITTED,
  "ADT_DECEASED" : DECEASED,
  "ADT_DISCHARGED" : DISCHARGED,
  "ADT_PAYOR_CHANGED" : PAYOR_CHANGED,
  "ADT_PRESENTED" : PRESENTED,
  "ADT_TRANSFERRED" : TRANSFERRED,
  "ADT_CLOSED" : CLOSED,

  // Referral statuses
  "OPENBEDS_REFERRAL_OPENED" : ReferralStatuses.Opened,
  "OPENBEDS_REFERRAL_CLOSED" : ReferralStatuses.Closed,
  "OPENBEDS_REFERRAL_ACCEPTED" : AcceptedStatuses.Accepted,
  "OPENBEDS_REFERRAL_DONE" : AcceptedStatuses.Done,
  "OPENBEDS_REFERRAL_DECLINED" : AcceptedStatuses.Declined,
  "OPENBEDS_REFERRAL_NO_SHOW" : ShowStatuses["No-Show"],
  "OPENBEDS_REFERRAL_SHOW" : ShowStatuses.Show,
  "OPENBEDS_REFERRAL_WAITLISTED" : ReferralStatuses.Waitlisted,
  "OPENBEDS_REFERRAL_PENDING" : ReferralStatuses.Pending,

  // Crisis statuses
  "OPENBEDS_CRISIS_REQUESTED" : CrisisStatuses.REQUESTED,
  "OPENBEDS_CRISIS_ACCEPTED" : CrisisStatuses.ACCEPTED,
  "OPENBEDS_CRISIS_ACKNOWLEDGED" : CrisisStatuses.ACKNOWLEDGED,
  "OPENBEDS_CRISIS_ARRIVED" : CrisisStatuses.ARRIVED,
  "OPENBEDS_CRISIS_DEPARTED" : CrisisStatuses.DEPARTED,
  "OPENBEDS_CRISIS_DECLINED" : CrisisStatuses.DECLINED,
  "OPENBEDS_CRISIS_CANCELLED" : CrisisStatuses.CANCELLED,
  "OPENBEDS_CRISIS_COMPLETED" : CrisisStatuses.COMPLETED,
  "OPENBEDS_CRISIS_INTAKE_COMPLETED" : CrisisStatuses.INTAKE_COMPLETED,
  "OPENBEDS_CRISIS_INTAKE_CANCELLED" : CrisisStatuses.INTAKE_CANCELLED,
  "OPENBEDS_CRISIS_CALL_STARTED" : CrisisStatuses.CALL_STARTED,
  "OPENBEDS_CRISIS_CALL_ENDED" : CrisisStatuses.CALL_ENDED,

  // Justice Involved statuses
  "JUSTICE_INVOLVED_BOOKED" : "Booked",
  "JUSTICE_INVOLVED_RELEASED" : "Released",

  // SDOH statuses
  "FINDHELP_SDOH_NEW_REFERRAL" : SDOHStatuses.NEW_REFERRAL,
  "FINDHELP_SDOH_PENDING" : SDOHStatuses.PENDING,
  "FINDHELP_SDOH_ACCEPTED" : SDOHStatuses.ACCEPTED,
  "FINDHELP_SDOH_DECLINED" : SDOHStatuses.DECLINED,


  "UNKNOWN" : "Unknown"
};

export const toUIEncounterStatus = (status?: string) =>
  EncounterStatusMap[status ?? "UNKNOWN"];

type Domain =
  | 'ADT'
  | 'REFERRAL'
  | 'CRISIS'
  | 'JI'
  | 'SDOH'
  | 'UNKNOWN';

const detectDomain = (raw?: string): Domain => {
  if (!raw) return 'UNKNOWN';
  if (raw.startsWith('ADT_')) return 'ADT';
  if (raw.startsWith('OPENBEDS_REFERRAL_')) return 'REFERRAL';
  if (raw.startsWith('OPENBEDS_CRISIS_')) return 'CRISIS';
  if (raw.startsWith('JUSTICE_INVOLVED_')) return 'JI';
  if (raw.startsWith('FINDHELP_SDOH_')) return 'SDOH';
  return 'UNKNOWN';
};

export const toUIStatusLabel = (raw: string): string => {
  const domain = detectDomain(raw);
  const key = toUIEncounterStatus(raw);
  if (!key || key === 'Unknown') return '';

  switch (domain) {
  case 'ADT':
    return Status[key]?.displayName ?? key;
  case 'REFERRAL':
    return ReferralStatus[key]?.displayName ?? key;
  case 'CRISIS':
    return CrisisStatus[key]?.displayName ?? key;
  case 'SDOH':
    return SDOHStatus[key]?.displayName ?? key;
  case 'JI':
    // Justice involved has string labels, no map needed
    return key;
  default:
    // Fallback order if domain unknown
    return (
      Status[key]?.displayName ??
        ReferralStatus[key]?.displayName ??
        CrisisStatus[key]?.displayName ??
        SDOHStatus[key]?.displayName ??
        ''
    );
  }
};


